import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Upload, Trash2, Coins } from 'lucide-react';

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/pas-deals`;
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

const DocThumb = ({ url, doc, token, onOpen }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!doc.content_type.startsWith('image/')) return undefined;
    let u;
    axios.get(url, { ...auth(token), responseType: 'blob' }).then(r => { u = URL.createObjectURL(r.data); setSrc(u); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [url, doc.content_type, token]);
  return src
    ? <img src={src} alt={doc.name} onClick={onOpen} className="w-16 h-16 object-cover rounded border border-slate-700 cursor-pointer" />
    : doc.content_type.startsWith('image/')
      ? <div className="w-16 h-16 rounded border border-slate-700 bg-slate-800 animate-pulse" />
      : <FileText onClick={onOpen} className="w-8 h-8 text-cyan-300 cursor-pointer" />;
};

export const DealDocs = ({ deal, token, admin, reload }) => {
  const docs = deal.documents || [];
  const url = (id) => `${BASE}/${admin ? 'admin/deals' : 'me'}/${deal.id}/documents/${id}`;
  const open = async (id) => {
    try { const r = await axios.get(url(id), { ...auth(token), responseType: 'blob' }); window.open(URL.createObjectURL(r.data), '_blank'); }
    catch { toast.error('Could not open document'); }
  };
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try { const r = await axios.post(`${BASE}/admin/deals/${deal.id}/documents`, fd, auth(token)); toast.success(r.data.message); reload(); }
    catch (e) { toast.error(e.response?.data?.detail?.toString() || 'Upload failed'); }
  };
  const remove = async (id) => {
    try { await axios.delete(url(id), auth(token)); toast.success('Document removed'); reload(); } catch { toast.error('Delete failed'); }
  };
  if (!admin && !docs.length) return null;
  return (
    <div className="space-y-2" data-testid={`deal-docs-${deal.id}`}>
      <p className="text-[10px] uppercase text-slate-500">Documents & photos ({docs.length})</p>
      <div className="flex flex-wrap gap-3">
        {docs.map(d => (
          <div key={d.id} className="flex flex-col items-center gap-1 w-24" data-testid={`deal-doc-${d.id}`}>
            <DocThumb url={url(d.id)} doc={d} token={token} onOpen={() => open(d.id)} />
            <button onClick={() => open(d.id)} className="text-[10px] text-cyan-300 truncate w-full text-center" data-testid={`deal-doc-open-${d.id}`}>{d.name}</button>
            {admin && <button onClick={() => remove(d.id)} className="text-red-400" data-testid={`deal-doc-delete-${d.id}`}><Trash2 className="w-3 h-3" /></button>}
          </div>
        ))}
      </div>
      {admin && (
        <label className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded text-xs cursor-pointer" data-testid={`deal-doc-upload-${deal.id}`}>
          <Upload className="w-3 h-3" /> Upload document or photo
          <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx,.xls,.xlsx" onChange={(e) => { upload(e.target.files[0]); e.target.value = ''; }} data-testid={`deal-doc-input-${deal.id}`} />
        </label>
      )}
    </div>
  );
};

export const DealPayoutsAdmin = ({ deal, token, reload }) => {
  const [rows, setRows] = useState([]);
  const [amt, setAmt] = useState('');
  const [note, setNote] = useState('');
  const load = () => axios.get(`${BASE}/admin/deals/${deal.id}/payouts`, auth(token)).then(r => setRows(r.data.payouts)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [deal.id]);
  const save = async () => {
    try { const r = await axios.post(`${BASE}/admin/deals/${deal.id}/payouts`, { amount_usd: Number(amt), note }, auth(token)); toast.success(r.data.message); setAmt(''); setNote(''); load(); reload(); }
    catch (e) { toast.error(e.response?.data?.detail?.toString() || 'Failed'); }
  };
  return (
    <div className="space-y-2" data-testid={`deal-payouts-${deal.id}`}>
      <p className="text-[10px] uppercase text-slate-500 flex items-center gap-1"><Coins className="w-3 h-3" /> Returns · target {deal.target_return_pct || 0}%/yr · {deal.term_months || 12} mo · paid {money(deal.paid_out_usd)}</p>
      <div className="flex flex-wrap gap-2">
        <input type="number" placeholder="Payout (USD)" value={amt} onChange={(e) => setAmt(e.target.value)} className="w-32 px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-xs" data-testid={`deal-payout-amt-${deal.id}`} />
        <input placeholder="Note (e.g. Q1 distribution)" value={note} onChange={(e) => setNote(e.target.value)} className="flex-1 min-w-[140px] px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-xs" data-testid={`deal-payout-note-${deal.id}`} />
        <button disabled={!Number(amt)} onClick={save} className="px-3 py-1 bg-amber-600 disabled:opacity-40 text-white rounded text-xs" data-testid={`deal-payout-save-${deal.id}`}>Record payout</button>
      </div>
      {rows.map(p => (
        <div key={p.id} className="text-[11px] text-slate-300 border-t border-slate-800 pt-1" data-testid={`deal-payout-row-${p.id}`}>
          <b>{money(p.amount_usd)}</b> · {p.note || 'Payout'} · {p.created_at.slice(0, 10)} — {p.shares.map(s => `${s.email}: ${money(s.amount_usd)}`).join(', ')}
        </div>
      ))}
    </div>
  );
};
